/**
 * A finger on the calendar scrolls; it moves a block only after it has held
 * still for 300 ms (`docs/activity.md` §10.1). The mouse needs none of this —
 * a press that moves is a drag at once.
 *
 * Once armed, the page stops scrolling under the finger: a non-passive
 * `touchmove` listener is the one way to say so after the touch has begun.
 */
export interface Hold {
  /** Held long enough: the moves from here on are a drag. */
  armed(): boolean
  /** Moved before it was armed: it was a scroll, and nothing should happen. */
  cancelled(): boolean
  end(): void
}

const HOLD_MS = 300
const SLOP_PX = 8

export function holdToDrag(event: { clientX: number; clientY: number }): Hold {
  const x0 = event.clientX
  const y0 = event.clientY
  let armed = false
  let cancelled = false
  const timer = window.setTimeout(() => {
    if (cancelled) return
    armed = true
    navigator.vibrate?.(8)
  }, HOLD_MS)
  const onMove = (next: PointerEvent) => {
    if (armed || cancelled) return
    if (Math.hypot(next.clientX - x0, next.clientY - y0) > SLOP_PX) {
      cancelled = true
      window.clearTimeout(timer)
    }
  }
  const stopScroll = (next: TouchEvent) => { if (armed) next.preventDefault() }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('touchmove', stopScroll, { passive: false })
  return {
    armed: () => armed,
    cancelled: () => cancelled,
    end: () => {
      window.clearTimeout(timer)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('touchmove', stopScroll)
    },
  }
}

/**
 * A finger held still and lifted — the iPad's right-click (Aaron, Oct 24, note
 * 1.7) — for something a finger cannot drag (a timed task's chip). A block
 * that drags reads the same hold in its own drag: lifted without moving, it
 * opens the menu rather than moving.
 */
export function holdForMenu(event: { pointerType: string; clientX: number; clientY: number }, open: (at: { x: number; y: number }) => void): void {
  if (event.pointerType !== 'touch') return
  const at = { x: event.clientX, y: event.clientY }
  const hold = holdToDrag(event)
  const up = (ended: PointerEvent) => {
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    const long = ended.type === 'pointerup' && hold.armed() && !hold.cancelled()
    hold.end()
    if (long) open(at)
  }
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
}
